import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  normalizeAccountingDisplayTimezone,
  type AccountingDisplayTimezone,
} from '@pokerroom/types';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';

export default function AccountingDisplayTimezonePanel({ venueId }: { venueId: string }) {
  const { t } = useTranslation('accounting');
  const actor = useAuthStore((s) => s.user?.id),
    revision = useAuthStore((s) => s.sessionRevision);
  const client = useQueryClient();
  const key = ['accounting-display-venue', actor, revision, venueId];
  const [draft, setDraft] = useState<string | null>(null);
  const query = useQuery({
    queryKey: key,
    queryFn: async ({ signal }) => {
      const result = await api.get<AccountingDisplayTimezone>(
        `/venues/${venueId}/accounting-display-timezone`,
        { signal },
      );
      if (result.venueId !== venueId || !normalizeAccountingDisplayTimezone(result.displayTimezone))
        throw new Error('Invalid display timezone response');
      return result;
    },
    retry: false,
    refetchOnWindowFocus: 'always',
  });
  const value = draft ?? query.data?.displayTimezone ?? '';
  const zone = normalizeAccountingDisplayTimezone(value);
  const mutation = useMutation({
    mutationFn: () => {
      if (!zone) throw new Error('Invalid timezone');
      return api.patch<AccountingDisplayTimezone>(
        `/venues/${venueId}/accounting-display-timezone`,
        { displayTimezone: zone },
      );
    },
    onSuccess: async (result) => {
      const current = useAuthStore.getState();
      if (
        current.user?.id !== actor ||
        current.sessionRevision !== revision ||
        result.venueId !== venueId
      )
        return;
      client.setQueryData(key, result);
      setDraft(null);
      await client.invalidateQueries({ queryKey: ['daily-ledgers'] });
      await client.invalidateQueries({ queryKey: ['accounting-history'] });
    },
  });
  return (
    <section
      aria-label={t('displayZone.title')}
      className="rounded-lg border border-base-300 p-3 space-y-3"
    >
      <h4 className="font-semibold">{t('displayZone.title')}</h4>
      <p>{t('displayZone.help')}</p>
      {query.isPending && <p role="status">{t('cashSummaryLoading')}</p>}
      {query.isError && (
        <div role="alert">
          {t('displayZone.loadFailed')}{' '}
          <button type="button" className="btn min-h-11" onClick={() => void query.refetch()}>
            {t('cashWorkflow.refresh')}
          </button>
        </div>
      )}
      {query.data && !query.isError && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (zone && !mutation.isPending && !query.isFetching) mutation.mutate();
          }}
        >
          <label className="block">
            {t('displayZone.label')}
            <input
              className="input input-bordered w-full"
              value={value}
              maxLength={64}
              required
              list="accounting-display-zones"
              disabled={mutation.isPending}
              onChange={(e) => {
                setDraft(e.target.value);
                mutation.reset();
              }}
            />
          </label>
          <datalist id="accounting-display-zones">
            <option value="Asia/Taipei" />
            <option value="Asia/Tokyo" />
            <option value="UTC" />
            <option value="America/New_York" />
          </datalist>
          {!zone && <p role="alert">{t('displayZone.invalid')}</p>}
          <div className="flex gap-2 mt-2">
            <button
              type="submit"
              className="btn min-h-11"
              disabled={
                !zone ||
                zone === query.data.displayTimezone ||
                query.isFetching ||
                mutation.isPending
              }
            >
              {t('displayZone.save')}
            </button>
            <button
              type="button"
              className="btn min-h-11"
              disabled={mutation.isPending}
              onClick={() => {
                setDraft(null);
                mutation.reset();
              }}
            >
              {t('displayZone.cancel')}
            </button>
          </div>
        </form>
      )}
      {mutation.isPending && <p role="status">{t('displayZone.saving')}</p>}
      {mutation.isError && <p role="alert">{t('displayZone.saveFailed')}</p>}
      {mutation.isSuccess && <p role="status">{t('displayZone.saved')}</p>}
    </section>
  );
}
